/**
 * Utility for resolving static F&O stock logos from the public folder.
 * 
 * Instructions for adding logos:
 * 1. Create a folder: `public/images/logos/`
 * 2. Save your 200+ F&O stock logos as PNGs in this folder.
 * 3. Name the files exactly as their NSE ticker symbol (e.g., `RELIANCE.png`, `TCS.png`, `HDFCBANK.png`).
 * 4. Add a `default.png` in that folder to act as a fallback for missing logos.
 */

const demoRemoteLogos = {
  'RELIANCE': 'https://logo.clearbit.com/relianceindustries.com',
  'TCS': 'https://logo.clearbit.com/tcs.com',
  'HDFCBANK': 'https://logo.clearbit.com/hdfcbank.com',
  'INFY': 'https://logo.clearbit.com/infosys.com',
};

export const getStockLogo = (ticker) => {
  if (!ticker) return '/images/logos/default.png';

  const cleanTicker = ticker.replace('.NS', '').trim().toUpperCase();

  // For demonstration: use remote logos if we haven't downloaded local ones yet
  if (demoRemoteLogos[cleanTicker]) {
    return demoRemoteLogos[cleanTicker];
  }

  return `/images/logos/${cleanTicker}.png`;
};

/**
 * An Image component that automatically falls back to the default logo 
 * if the specific stock's PNG is missing from the folder.
 * 
 * Usage:
 * <StockLogo ticker="RELIANCE" className="w-8 h-8 rounded-full" />
 */
import React, { useState } from 'react';

export const StockLogo = ({ ticker, className, alt, style }) => {
  const [imgSrc, setImgSrc] = useState(getStockLogo(ticker));

  const handleError = () => {
    // If the specific logo fails to load (file doesn't exist), switch to default
    if (imgSrc !== '/images/logos/default.png') {
      setImgSrc('/images/logos/default.png');
    }
  };

  return (
    <img 
      src={imgSrc} 
      alt={alt || `${ticker} Logo`} 
      className={className || "stock-logo"} 
      style={style}
      onError={handleError}
    />
  );
};
